import React, { useState } from 'react';
import { ArrowRight, BookOpen, GraduationCap, Building2, CheckCircle } from 'lucide-react';

const universities = {
  eth: { 
    name: "ETH Zürich", 
    hasRules: true, 
    rules: { 
      bachelor: "Hardcover oder Klebebindung", 
      master: "Hardcover empfohlen", 
      diss: "Hardcover Pflicht" 
    }
  },
  uzh: { 
    name: "Universität Zürich", 
    hasRules: true, 
    rules: { 
      bachelor: "ZB-Norm Klebebindung oder Hardcover", 
      master: "ZB-Norm Hardcover empfohlen", 
      diss: "ZB-Norm Hardcover" 
    }
  },
  zhaw: { 
    name: "ZHAW", 
    hasRules: true, 
    rules: { 
      bachelor: "Klebebindung oder Spiralbindung", 
      master: "Klebebindung empfohlen", 
      diss: "Hardcover" 
    }
  },
  phzh: { 
    name: "PH Zürich", 
    hasRules: true, 
    rules: { 
      bachelor: "Klebebindung", 
      master: "Klebebindung oder Hardcover", 
      diss: "Hardcover" 
    }
  },
  hslu: { name: "HSLU", hasRules: false },
  fhnw: { name: "FHNW", hasRules: false },
  unibe: { name: "Uni Bern", hasRules: false },
  other: { name: "Andere Hochschule", hasRules: false }
};

const products = {
  hardcover: { 
    name: "Hardcover Klassisch", 
    url: "https://adagmedia.ch/hardcover-bindung.html", 
    desc: "Edel & robust - der Klassiker für wichtige Arbeiten",
    price: "ab CHF 39.-"
  },
  "zb-hardcover": { 
    name: "ZB-Norm Hardcover", 
    url: "https://adagmedia.ch/zb-norm-hardcover.html", 
    desc: "Spezialformat für UZH/Zentralbibliothek",
    price: "ab CHF 49.-"
  },
  klebebindung: { 
    name: "Klebebindung", 
    url: "https://adagmedia.ch/klebebindung.html", 
    desc: "Klassisch & professionell",
    price: "ab CHF 19.-"
  },
  "zb-klebe": { 
    name: "ZB-Norm Klebebindung", 
    url: "https://adagmedia.ch/zbnorm-klebebindung.html", 
    desc: "UZH Standard mit Bibliotheksnorm",
    price: "ab CHF 25.-"
  },
  softcover: { 
    name: "Softcover", 
    url: "https://adagmedia.ch/softcover.html", 
    desc: "Günstig & praktisch",
    price: "ab CHF 15.-"
  },
  spiral: { 
    name: "Spiralbindung", 
    url: "https://adagmedia.ch/metallspiral-bindung.html", 
    desc: "Praktisch & flexibel - liegt flach auf",
    price: "ab CHF 12.-"
  }
};

const preferences = [
  { key: "edel", label: "Edel & hochwertig", rec: "hardcover" },
  { key: "klassisch", label: "Klassisch & professionell", rec: "klebebindung" },
  { key: "praktisch", label: "Praktisch & flexibel", rec: "spiral" },
  { key: "guenstig", label: "Günstig", rec: "softcover" }
];

export default function BindungsBerater() {
  const [step, setStep] = useState(1);
  const [selectedUni, setSelectedUni] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [recommendation, setRecommendation] = useState(null);
  const [rule, setRule] = useState(null);

  const handleUniSelect = (uniKey) => {
    setSelectedUni(uniKey);
    const uni = universities[uniKey];
    if (uni.hasRules) {
      setStep(2);
    } else {
      setStep(3);
    }
  };

  const handleLevelSelect = (level) => {
    setSelectedLevel(level);
    const uni = universities[selectedUni];
    const ruleText = uni.rules[level];
    setRule(ruleText);
    
    let rec = "klebebindung";
    if (ruleText.toLowerCase().includes("hardcover")) {
      rec = selectedUni === "uzh" ? "zb-hardcover" : "hardcover";
    }
    if (ruleText.toLowerCase().includes("zb-norm klebe")) {
      rec = "zb-klebe";
    }
    if (ruleText.toLowerCase().includes("spiral")) {
      rec = "spiral";
    }
    
    setRecommendation(rec);
    setStep(4);
  };

  const handlePrefSelect = (prefKey) => {
    const pref = preferences.find(p => p.key === prefKey);
    setRecommendation(pref.rec);
    setStep(4);
  };

  const reset = () => {
    setStep(1);
    setSelectedUni(null);
    setSelectedLevel(null);
    setRecommendation(null);
    setRule(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-2xl mb-4">
            <BookOpen className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Bindungs-Berater</h1>
          <p className="text-gray-600">Finde die perfekte Bindung für deine Abschlussarbeit</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Progress */}
          <div className="flex items-center justify-center gap-2 mb-8">
            {[1, 2, 3, 4].map(s => (
              <div 
                key={s}
                className={`w-3 h-3 rounded-full transition-all ${
                  s <= step ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>

          {/* Step 1: Hochschule wählen */}
          {step === 1 && (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-blue-600" />
                An welcher Hochschule studierst du?
              </h2>
              <p className="text-gray-500 text-sm mb-6">Wir prüfen ob es Vorschriften gibt</p>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {Object.entries(universities).map(([key, uni]) => (
                  <button
                    key={key}
                    onClick={() => handleUniSelect(key)}
                    className="p-4 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
                  >
                    <div className="font-medium text-gray-900">{uni.name}</div>
                    {uni.hasRules && (
                      <div className="text-xs text-orange-600 mt-1">Hat Vorschriften</div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Arbeitsstufe (nur wenn Vorschriften) */}
          {step === 2 && (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-600" />
                {universities[selectedUni]?.name}
              </h2>
              <p className="text-gray-500 text-sm mb-6">Welche Art von Arbeit schreibst du?</p>
              
              <div className="grid grid-cols-3 gap-3">
                <button
                  onClick={() => handleLevelSelect('bachelor')}
                  className="p-4 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all"
                >
                  <div className="font-medium text-gray-900">Bachelor</div>
                </button>
                <button
                  onClick={() => handleLevelSelect('master')}
                  className="p-4 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all"
                >
                  <div className="font-medium text-gray-900">Master</div>
                </button>
                <button
                  onClick={() => handleLevelSelect('diss')}
                  className="p-4 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all"
                >
                  <div className="font-medium text-gray-900">Dissertation</div>
                </button>
              </div>
              
              <button onClick={() => setStep(1)} className="mt-4 text-sm text-gray-500 hover:text-blue-600">
                ← Andere Hochschule
              </button>
            </div>
          )}

          {/* Step 3: Präferenz (wenn keine Vorschriften) */}
          {step === 3 && (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                {universities[selectedUni]?.name} - Keine Vorschriften!
              </h2>
              <p className="text-gray-500 text-sm mb-6">Du kannst frei wählen. Was ist dir wichtig?</p>
              
              <div className="grid grid-cols-2 gap-3">
                {preferences.map(pref => (
                  <button
                    key={pref.key}
                    onClick={() => handlePrefSelect(pref.key)}
                    className="p-4 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
                  >
                    <div className="font-medium text-gray-900">{pref.label}</div>
                  </button>
                ))}
              </div>
              
              <button onClick={() => setStep(1)} className="mt-4 text-sm text-gray-500 hover:text-blue-600">
                ← Andere Hochschule
              </button>
            </div>
          )}

          {/* Step 4: Empfehlung */}
          {step === 4 && recommendation && (
            <div>
              <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6 mb-6">
                <div className="flex items-center gap-2 text-green-700 font-semibold mb-2">
                  <CheckCircle className="w-5 h-5" />
                  Unsere Empfehlung
                </div>
                
                {rule && (
                  <div className="text-sm text-gray-600 mb-3 bg-white rounded-lg p-3">
                    <span className="font-medium">Vorschrift:</span> {rule}
                  </div>
                )}
                
                <div className="text-2xl font-bold text-gray-900 mb-1">
                  {products[recommendation]?.name}
                </div>
                <div className="text-gray-600 mb-2">
                  {products[recommendation]?.desc}
                </div>
                <div className="text-blue-600 font-semibold">
                  {products[recommendation]?.price}
                </div>
              </div>
              
              <a
                href={products[recommendation]?.url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white py-4 px-6 rounded-xl font-semibold transition-all"
              >
                Jetzt bestellen
                <ArrowRight className="w-5 h-5" />
              </a>
              
              <button 
                onClick={reset} 
                className="w-full mt-3 text-gray-500 hover:text-blue-600 py-2"
              >
                Nochmal starten
              </button>
            </div>
          )}
        </div>

        <div className="text-center mt-6 text-sm text-gray-500">
          Powered by ADAG Print · Universitätstrasse 25, 8006 Zürich
        </div>
      </div>
    </div>
  );
}
